<script setup lang="ts">
import { Head, Link } from "@inertiajs/vue3";
import { computed, ref, toRefs, watch } from "vue";
import { z } from "zod";

import BaseSpinner from "@/components/BaseSpinner.vue";
import FavIcon from "@/components/FavIcon.vue";
import { useValidatedProps } from "@/composable";
import {
  reportMonthSchema,
  type ReportMunicipality,
  reportMunicipalitySchema,
  type ReportQuestionnaire,
  reportQuestionnaireSchema,
} from "@/types";

const { questionnaires, municipalities, months, defaultPeriod } = toRefs(
  useValidatedProps(
    z.object({
      questionnaires: z.array(reportQuestionnaireSchema),
      municipalities: z.array(reportMunicipalitySchema),
      months: z.array(reportMonthSchema),
      defaultPeriod: z.object({
        from: reportMonthSchema,
        to: reportMonthSchema,
      }),
    }),
  ),
);

const monthFormat = new Intl.DateTimeFormat("da-DK", {
  month: "long",
  year: "numeric",
});

function monthLabel(month: string) {
  const [year, monthNumber] = month.split("-").map(Number);

  return monthFormat.format(new Date(year ?? 0, (monthNumber ?? 1) - 1));
}

const fromMonth = ref(defaultPeriod.value.from);
const toMonth = ref(defaultPeriod.value.to);

watch(fromMonth, (from) => {
  if (from > toMonth.value) {
    toMonth.value = from;
  }
});

watch(toMonth, (to) => {
  if (to < fromMonth.value) {
    fromMonth.value = to;
  }
});

const selectedQuestionnaireIds = ref<ReportQuestionnaire["id"][]>([]);
const selectedMunicipalityIds = ref<ReportMunicipality["id"][]>([]);
const isDownloading = ref(false);
const errorMessage = ref<string>();

const regionNames = computed(() =>
  [
    ...new Set(
      municipalities.value
        .map((municipality) => municipality.regionName)
        .filter((regionName) => regionName !== null),
    ),
  ].sort((a, b) => a.localeCompare(b, "da")),
);

const canDownload = computed(
  () =>
    selectedQuestionnaireIds.value.length > 0 &&
    selectedMunicipalityIds.value.length > 0 &&
    !isDownloading.value,
);

function selectAllQuestionnaires(selected: boolean) {
  selectedQuestionnaireIds.value = selected
    ? questionnaires.value.map((questionnaire) => questionnaire.id)
    : [];
}

function selectAllMunicipalities(selected: boolean) {
  selectedMunicipalityIds.value = selected
    ? municipalities.value.map((municipality) => municipality.id)
    : [];
}

function selectRegion(regionName: string) {
  const regionIds = municipalities.value
    .filter((municipality) => municipality.regionName === regionName)
    .map((municipality) => municipality.id);

  selectedMunicipalityIds.value = [
    ...new Set([...selectedMunicipalityIds.value, ...regionIds]),
  ];
}

function filenameFrom(response: Response) {
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = /filename="?([^";]+)"?/.exec(disposition);

  return match?.[1] ?? "kommunal-statistikrapport.pdf";
}

async function download() {
  const params = new URLSearchParams({
    from_month: fromMonth.value,
    to_month: toMonth.value,
  });

  for (const id of selectedQuestionnaireIds.value) {
    params.append("questionnaire_ids[]", id);
  }

  for (const id of selectedMunicipalityIds.value) {
    params.append("municipality_ids[]", String(id));
  }

  isDownloading.value = true;
  errorMessage.value = undefined;

  try {
    const response = await fetch(`/municipal_report.pdf?${params.toString()}`, {
      headers: { Accept: "application/pdf" },
    });

    if (!response.ok) {
      throw new Error(`Uventet svar fra serveren (${response.status})`);
    }

    const url = URL.createObjectURL(await response.blob());
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = filenameFrom(response);
    anchor.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    errorMessage.value =
      error instanceof Error
        ? `Rapporten kunne ikke hentes: ${error.message}`
        : "Rapporten kunne ikke hentes.";
  } finally {
    isDownloading.value = false;
  }
}
</script>

<template>
  <Head title="Kommunale rapporter" />

  <div class="container py-4">
    <header class="d-flex align-items-center gap-3 mb-4">
      <FavIcon />
      <div>
        <h1 class="h3 mb-0">Kommunale rapporter</h1>
        <Link href="/" class="link-secondary small">
          <i class="bi bi-arrow-left"></i> Tilbage til kortet
        </Link>
      </div>
    </header>

    <p>
      Vælg periode, spørgeskemaer og kommuner, der skal indgå i rapporten.
      Rapporten viser delte besvarelser pr. måned i perioden for hver valgt
      kommune. Perioden kan højst være de seneste {{ months.length }} måneder.
    </p>

    <div class="row g-4">
      <div class="col-md-5">
        <section class="mb-4">
          <div class="d-flex align-items-center py-1 mb-2">
            <h2 class="h5 mb-0">Periode</h2>
          </div>
          <div class="row g-2">
            <div class="col-sm-6">
              <label for="period-from" class="form-label small">Fra</label>
              <select id="period-from" v-model="fromMonth" class="form-select">
                <option v-for="month in months" :key="month" :value="month">
                  {{ monthLabel(month) }}
                </option>
              </select>
            </div>
            <div class="col-sm-6">
              <label for="period-to" class="form-label small">Til</label>
              <select id="period-to" v-model="toMonth" class="form-select">
                <option v-for="month in months" :key="month" :value="month">
                  {{ monthLabel(month) }}
                </option>
              </select>
            </div>
          </div>
        </section>

        <section>
          <div class="d-flex align-items-center justify-content-between mb-2">
            <h2 class="h5 mb-0">Spørgeskemaer</h2>
            <div class="btn-group btn-group-sm">
              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="selectAllQuestionnaires(true)"
              >
                Vælg alle
              </button>
              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="selectAllQuestionnaires(false)"
              >
                Fravælg alle
              </button>
            </div>
          </div>
          <p v-if="questionnaires.length === 0" class="text-muted">
            Ingen spørgeskemaer med delte besvarelser fra kommuner de seneste
            {{ months.length }} måneder.
          </p>
          <div
            v-else
            class="border rounded p-2 overflow-auto"
            style="max-height: 60vh"
          >
            <div
              v-for="questionnaire in questionnaires"
              :key="questionnaire.id"
              class="form-check"
            >
              <input
                :id="`questionnaire-${questionnaire.id}`"
                v-model="selectedQuestionnaireIds"
                class="form-check-input"
                type="checkbox"
                :value="questionnaire.id"
              />
              <label
                class="form-check-label"
                :for="`questionnaire-${questionnaire.id}`"
              >
                {{ questionnaire.name }}
              </label>
            </div>
          </div>
        </section>
      </div>

      <section class="col-md-7">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <h2 class="h5 mb-0">Kommuner</h2>
          <div class="btn-group btn-group-sm">
            <button
              type="button"
              class="btn btn-outline-secondary"
              @click="selectAllMunicipalities(true)"
            >
              Vælg alle
            </button>
            <button
              type="button"
              class="btn btn-outline-secondary"
              @click="selectAllMunicipalities(false)"
            >
              Fravælg alle
            </button>
          </div>
        </div>
        <div v-if="regionNames.length > 0" class="d-flex flex-wrap gap-2 mb-2">
          <button
            v-for="regionName in regionNames"
            :key="regionName"
            type="button"
            class="btn btn-sm btn-outline-primary"
            @click="selectRegion(regionName)"
          >
            <i class="bi bi-plus"></i> {{ regionName }}
          </button>
        </div>
        <div class="border rounded p-2 overflow-auto" style="max-height: 60vh">
          <div class="row row-cols-1 row-cols-sm-2 g-0">
            <div
              v-for="municipality in municipalities"
              :key="municipality.id"
              class="col form-check ms-0 ps-4"
            >
              <input
                :id="`municipality-${municipality.id}`"
                v-model="selectedMunicipalityIds"
                class="form-check-input"
                type="checkbox"
                :value="municipality.id"
              />
              <label
                class="form-check-label"
                :for="`municipality-${municipality.id}`"
              >
                {{ municipality.name }}
              </label>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div class="d-flex align-items-center gap-3 mt-4">
      <button
        type="button"
        class="btn btn-primary d-flex align-items-center gap-2"
        :disabled="!canDownload"
        @click="download()"
      >
        <BaseSpinner v-if="isDownloading" class="spinner-border-sm" />
        <i v-else class="bi bi-file-earmark-pdf"></i>
        Hent PDF-rapport
      </button>
      <span class="text-muted small">
        {{ selectedQuestionnaireIds.length }} spørgeskema(er) og
        {{ selectedMunicipalityIds.length }} kommune(r) valgt
      </span>
    </div>
    <div v-if="errorMessage" class="alert alert-danger mt-3" role="alert">
      {{ errorMessage }}
    </div>
  </div>
</template>
